import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import DayPicker from "../../../components/calendar/DayPicker";
import WeekPicker from "../../../components/calendar/WeekPicker";
import {
  startOfDayISO,
  startOfWeek,
  endOfWeek,
  addDays,
  formatDateFriendly,
  formatRange,
} from "../../../lib/dateUtil";
import DateCalendar from "../../../components/DateCalendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";

interface CalendarHeaderProps {
  startDate: string;
  endDate: string;
  status: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onStatusChange: (status: string) => void;
  onStaffChange: (staff: string | null) => void;
  staffList: { name: string; color: string }[];
  view: "day" | "week";
  setView: (view: "day" | "week") => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

const weekdayNamesShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  startDate,
  endDate,
  status,
  onStartDateChange,
  onEndDateChange,
  onStatusChange,
  onStaffChange,
  staffList,
  view,
  setView,
  selectedDate,
  setSelectedDate,
}) => {
  const weekStartsOnMonday = true;

  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);

  // Auto-select first staff if in week view and "All Staff" is selected
  React.useEffect(() => {
    if (
      view === "week" &&
      (selectedStaff === null || selectedStaff === "all")
    ) {
      if (staffList.length > 0) {
        const firstStaff = staffList[0].name;
        setSelectedStaff(firstStaff);
        onStaffChange(firstStaff);
      }
    }
  }, [view, staffList, selectedStaff, onStaffChange]);

  const updateParentDates = (date: Date, mode: "day" | "week") => {
    if (mode === "day") {
      const iso = startOfDayISO(date);
      onStartDateChange(iso);
      onEndDateChange(iso);
    } else {
      const s = startOfWeek(date, weekStartsOnMonday);
      const e = endOfWeek(date, weekStartsOnMonday);
      onStartDateChange(startOfDayISO(s));
      onEndDateChange(startOfDayISO(e));
    }
  };

  const prevWeek = () => {
    setView("week");
    const newDate = addDays(selectedDate, view === "day" ? -1 : -7);
    setSelectedDate(newDate);
    updateParentDates(newDate, view);
  };

  const nextWeek = () => {
    setView("week");
    const newDate = addDays(selectedDate, view === "day" ? 1 : 7);
    setSelectedDate(newDate);
    updateParentDates(newDate, view);
  };

  const handleWeekSelect = (s: Date, e: Date) => {
    setSelectedDate(s);
    setView("week");
    setWeekPickerOpen(false);
    updateParentDates(s, "week");
  };

  const handleDaySelect = (d: Date) => {
    setSelectedDate(d);
    setView("day");
    setDayPickerOpen(false);
    updateParentDates(d, "day");
  };

  const topLabel = () => {
    const formatUK = (d: Date) =>
      d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

    if (view === "day") return formatUK(selectedDate);

    const s = startOfWeek(selectedDate, weekStartsOnMonday);
    const e = endOfWeek(selectedDate, weekStartsOnMonday);

    return `${formatUK(s)} – ${formatUK(e)}`;
  };

  const handleQuickButton = (label: string) => {
    let target = new Date();

    if (label === "Today") {
      target = new Date();
    } else {
      const weekStart = startOfWeek(selectedDate, true);
      const dayIndexMap: Record<string, number> = {
        Mon: 0,
        Tue: 1,
        Wed: 2,
        Thu: 3,
        Fri: 4,
        Sat: 5,
        Sun: 6,
      };
      const idx = dayIndexMap[label];
      if (idx === undefined) return;

      target = addDays(weekStart, idx);
    }

    setSelectedDate(target);
    setView("day");
    updateParentDates(target, "day");
  };

  return (
    <div className="w-full border-b ">
      {/* Top Row */}
      <div className="bg-white border-b border-gray-200 px-2 py-3">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full md:w-auto">
            {/* Staff Select */}
            <Select
              value={selectedStaff || "all"}
              onValueChange={(val) => {
                const staff = val === "all" ? null : val;
                setSelectedStaff(staff);
                onStaffChange(staff);
              }}
            >
              <SelectTrigger className="w-full md:w-36 h-10 md:h-9">
                <SelectValue placeholder="All Staff" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Staff</SelectItem>
                {staffList.map((s) => (
                  <SelectItem key={s.name || Math.random()} value={s.name}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title - Order last on mobile so it's below inputs, or second */}
          <h1 className="text-xl md:text-lg font-semibold order-last md:order-none">
            {topLabel()}
          </h1>

          {/* View Toggles & Date Picker */}
          <div className="flex items-center justify-between md:justify-end w-full md:w-auto gap-2">
            <div className="flex border border-purple-300 rounded-lg overflow-hidden flex-1 md:flex-none">
              <Button
                variant={view === "week" ? "ghost" : "outline"}
                size="sm"
                onClick={() => setView("week")}
                className={`flex-1 md:flex-none h-9 px-4 rounded-none text-gray-800 text-sm md:text-[16px] font-normal ${view === "week" ? "bg-purple-100" : ""
                  }`}
              >
                Week
              </Button>

              <Button
                variant={view === "day" ? "ghost" : "outline"}
                size="sm"
                onClick={() => setView("day")}
                className={`flex-1 md:flex-none h-9 px-4 rounded-none text-gray-800 text-sm md:text-[16px] font-normal ${view === "day" ? "bg-purple-100" : ""
                  }`}
              >
                Day
              </Button>
            </div>

            <Popover
              open={view === "week" ? weekPickerOpen : dayPickerOpen}
              onOpenChange={(open) => {
                if (view === "week") setWeekPickerOpen(open);
                else setDayPickerOpen(open);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1 border-purple-300 text-gray-800 text-sm md:text-[16px] font-normal px-3"
                >
                  <Calendar className="h-4 w-4 text-[#885ABB]" />
                  <span className="hidden sm:inline">Date</span>
                </Button>
              </PopoverTrigger>

              <PopoverContent className="p-0 bg-transparent shadow-none">
                {view === "week" ? (
                  <WeekPicker
                    initialDate={selectedDate}
                    weekStartsOnMonday={true}
                    onSelectWeek={handleWeekSelect}
                    onClose={() => setWeekPickerOpen(false)}
                  />
                ) : (
                  <DateCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date: Date) => {
                      setSelectedDate(date);
                      setView("day");
                      updateParentDates(date, "day");
                      setDayPickerOpen(false);
                    }}
                  />
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="flex flex-col md:flex-row items-center justify-between px-2 py-2 gap-3">
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-10 px-3 border-purple-300 text-gray-800 text-[16px] font-normal bg-white"
            onClick={prevWeek}
          >
            <ChevronLeft
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
            <ChevronLeft
              className="h-5 w-5 -ml-3"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
            Week
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-10 px-3 border-purple-300 bg-white text-gray-800 text-[16px] font-normal"
            onClick={() => {
              const newDate = addDays(selectedDate, -1);
              setSelectedDate(newDate);
              setView("day");
              updateParentDates(newDate, "day");
            }}
          >
            <ChevronLeft
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />{" "}
            Day
          </Button>

          <div className="hidden lg:flex rounded-md overflow-hidden bg-white">
            {["Today", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
              (day, idx) => (
                <Button
                  key={day}
                  variant="outline"
                  size="sm"
                  className={`h-10 px-4 border-purple-300 text-gray-800 text-[16px] font-normal ${idx !== 0 ? "border-l-0" : "first:rounded-l-md"
                    } ${idx === 6 ? "last:rounded-r-md" : ""}`}
                  onClick={() => handleQuickButton(day)}
                >
                  {day}
                </Button>
              )
            )}
          </div>

          {/* Tablet Only "Today" Button */}
          <Button
            variant="outline"
            size="sm"
            className="hidden md:flex lg:hidden h-10 px-4 border-purple-300 text-gray-800 text-[16px] font-normal bg-white"
            onClick={() => handleQuickButton("Today")}
          >
            Today
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="h-10 px-3 border-purple-300 bg-white text-gray-800 text-[16px] font-normal"
            onClick={() => {
              const newDate = addDays(selectedDate, 1);
              setSelectedDate(newDate);
              setView("day");
              updateParentDates(newDate, "day");
            }}
          >
            Day{" "}
            <ChevronRight
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-10 px-3 border-purple-300 bg-white text-gray-800 text-[16px] font-normal"
            onClick={nextWeek}
          >
            Week{" "}
            <ChevronRight
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
            <ChevronRight
              className="h-5 w-5 -ml-3"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
          </Button>
        </div>

        {/* Mobile/Tablet Navigation (Simplified) */}
        <div className="flex lg:hidden items-center justify-between w-full md:w-auto md:flex-1 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10 border-purple-300 bg-white text-gray-800 text-[16px] font-normal px-3"
            onClick={() => {
              const daysToSubtract = view === "day" ? -1 : -7;
              const newDate = addDays(selectedDate, daysToSubtract);
              setSelectedDate(newDate);
              updateParentDates(newDate, view);
            }}
          >
            <ChevronLeft
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />{" "}
            Prev
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10 border-purple-300 bg-white text-gray-800 text-[16px] font-normal px-3"
            onClick={() => handleQuickButton("Today")}
          >
            Today
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10 border-purple-300 bg-white text-gray-800 text-[16px] font-normal px-3"
            onClick={() => {
              const daysToAdd = view === "day" ? 1 : 7;
              const newDate = addDays(selectedDate, daysToAdd);
              setSelectedDate(newDate);
              updateParentDates(newDate, view);
            }}
          >
            Next{" "}
            <ChevronRight
              className="h-5 w-5"
              style={{ color: "#885ABB" }}
              strokeWidth={4}
            />
          </Button>
        </div>

        {/* Status Select */}
        <div className="flex flex-col w-full md:w-[200px]">
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger className="border-purple-300 bg-white h-10">
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">View All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="no_show">No Show</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default CalendarHeader;
